import { Checkbox, FormInput, PrimaryButton, SuccessButton } from '@systemeio/ui'
import { createLecture } from 'modules/courses/courses/curriculum/api/lecture-api'
import { useLectureTemplate } from 'modules/courses/courses/curriculum/hooks/use-lecture-templates'
import { CourseCurriculumInterface } from 'modules/courses/courses/curriculum/types/curriculum-interface'
import {
  CreateLectureErrorsInterface,
  CreateLectureInterface,
  LectureTemplateInterface,
} from 'modules/courses/courses/curriculum/types/lecture-interface'
import React, { memo, useEffect, useState } from 'react'
import CreateModal from 'shared/components/create-modal'
import {
  FieldErrorAndDescription,
  FieldErrorAndDescriptionProps,
} from 'shared/components/form/field-error-and-description'
import { FieldLabel, FieldLabelProps } from 'shared/components/form/field-label'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { Loader } from 'shared/components/loader'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { fetchWithFieldErrors } from 'shared/utils/fetch-with-errors'
import { KeyedMutator } from 'swr'
import { AddLectureButton } from './add-lecture-button'
import { CheckCircleIcon, EyeIcon } from '@systemeio/ui/icons'

const defaultErrors: CreateLectureErrorsInterface = {
  fields: {
    name: '',
    pageTemplate: '',
    delayBeforePreviousLecture: '',
  },
}
const defaultValues: CreateLectureInterface = {
  name: '',
  delayBeforePreviousLecture: 0,
  showComments: false,
  pageTemplate: undefined,
}

interface CreateLectureModalInterface {
  moduleId: number
  mutate: KeyedMutator<CourseCurriculumInterface>
}

const CreateLectureModal = ({ moduleId, mutate }: CreateLectureModalInterface) => {
  const { t } = useLocoTranslation()
  const [opened, setOpened] = useState(false)
  const [errors, setErrors] = useState(defaultErrors)

  const { revalidateModules } = useRevalidateModules()

  const afterLeave = () => {
    setErrors(defaultErrors)
  }

  const onClose = () => setOpened(false)

  const onCreate = async (data: CreateLectureInterface) => {
    const lectureData = {
      ...data,
      delayBeforePreviousLecture: data.delayBeforePreviousLecture
        ? data.delayBeforePreviousLecture
        : 0,
    }
    await fetchWithFieldErrors(async () => {
      const response = await createLecture(moduleId, lectureData)
      await mutate(data => {
        if (data) {
          return {
            ...data,
            modules: data.modules.map(module => {
              if (module.id === moduleId) {
                return { ...module, lectures: [...module.lectures, response.data] }
              }
              return module
            }),
          }
        }
      }, false)
    }, setErrors)
    await revalidateModules([RequestModuleEnum.MembershipCourseLecture])
  }

  return (
    <>
      <AddLectureButton onClick={() => setOpened(true)} data-test={'module-create-lecture'} />
      <CreateModal
        opened={opened}
        onClose={onClose}
        afterLeave={afterLeave}
        defaultValues={defaultValues}
        fullCaption={t('dashboard.course.lecture.create')}
        caption={t('dashboard.course.lecture_title')}
        creatable={[
          {
            field: 'name',
            onRender: (value, onChange) => (
              <FormInput
                key={`${moduleId}-name`}
                label={t('global.name')}
                value={value}
                required
                onChange={e => onChange(e.target.value)}
                error={errors.fields.name}
                data-test={'module-create-lecture-name'}
              />
            ),
          },
          {
            field: 'delayBeforePreviousLecture',
            onRender: (value, onChange) => (
              <FormInput
                key={`${moduleId}-delayBeforePreviousLecture`}
                label={t('dashboard.course.lecture_delay_title')}
                value={value}
                type="number"
                onChange={e => onChange(Number(e.target.value))}
                error={errors.fields.delayBeforePreviousLecture}
                data-test={'module-create-lecture-delay'}
              />
            ),
          },
          {
            field: 'showComments',
            onRender: (value, onChange) => (
              <Checkbox
                key={`${moduleId}-showComments`}
                label={t('dashboard.course.show_comments_title')}
                value={value}
                onChange={onChange}
                data-test={'module-create-lecture-showComments'}
              />
            ),
          },
          {
            field: 'pageTemplate',
            onRender: (value, onChange) => (
              <TemplateSelector
                key={`${moduleId}-pageTemplate`}
                label={t('dashboard.course.lecture.select_template_title')}
                value={value}
                onChange={onChange}
                error={errors.fields.pageTemplate}
                data-test={'module-create-lecture-pageTemplate'}
              />
            ),
          },
        ]}
        onCreate={onCreate}
        requiredFields={['name', 'pageTemplate']}
        dataTestKey={'module-create-lecture'}
      />
    </>
  )
}

type TemplateSelectorProps = FieldErrorAndDescriptionProps &
  FieldLabelProps & {
    value?: LectureTemplateInterface['id']
    onChange: (id: LectureTemplateInterface['id']) => void
  }

const TemplateSelector = ({
  value,
  onChange,
  error,
  label,
  required = true,
}: TemplateSelectorProps) => {
  const { t } = useLocoTranslation()
  const { data: templates } = useLectureTemplate()

  useEffect(() => {
    if (templates && templates[0]) {
      onChange(templates[0].id)
    }
  }, [templates])

  return (
    <>
      {templates ? (
        templates.length === 0 ? (
          <span className={'flex justify-center'}>
            {t('dashboard.course.lecture.templates_not_found')}
          </span>
        ) : (
          <div className="flex flex-col gap-1">
            <FieldLabel label={label} required={required} />
            <div className={`overflow-auto`}>
              <ul
                className={`flex flex-col flex-wrap items-center justify-center gap-5 pb-5 sm:flex-row`}
              >
                {templates?.map(template => {
                  return (
                    <li key={template.id}>
                      <div
                        className={`focus-visible:border-brand-primary group relative flex h-[250px] w-[230px] flex-col items-center justify-start overflow-hidden rounded-lg border-2 border-transparent shadow-lg outline-hidden sm:h-[260px] sm:w-[230px] ${
                          template.id === value && 'border-green'
                        } main-transition-colors`}
                      >
                        <div
                          className={`z-1 bg-background-secondary/70 h-full w-full rounded-lg ${
                            value === template.id ? '' : 'hidden group-hover:flex'
                          } flex flex-col items-center justify-center gap-5`}
                        >
                          <PrimaryButton
                            as={LinkWithoutPrefetch}
                            href={`/page/template/${template.id}/show`}
                            target={'_blank'}
                            className={`max-w-[180px] break-all`}
                            type="button"
                            data-test={'module-create-lecture-showTemplate'}
                          >
                            <EyeIcon className={`fill-background-secondary h-[20px] w-[20px]`} />
                            {t('dashboard.course.lecture.preview_template')}
                          </PrimaryButton>
                          {template.id === value ? (
                            <div className={`flex h-[40px] max-w-[180px] items-center gap-1`}>
                              <CheckCircleIcon className={'fill-green'} />
                              <span className={'text-green break-all font-bold'}>
                                {t('dashboard.course.lecture.template_selected')}
                              </span>
                            </div>
                          ) : (
                            <SuccessButton
                              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.preventDefault()
                                onChange(template.id)
                              }}
                              className={'max-w-[180px]'}
                              type="button"
                            >
                              <CheckCircleIcon
                                className={'fill-background-secondary h-[20px] w-[20px]'}
                              />
                              <span className={'text-background-secondary break-all'}>
                                {t('dashboard.course.lecture.select_template')}
                              </span>
                            </SuccessButton>
                          )}
                        </div>
                        <img src={template.previewImage || ''} className={`absolute rounded-lg`} />
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        )
      ) : (
        <div className={'flex justify-center'}>
          <Loader />
        </div>
      )}
      <FieldErrorAndDescription error={error} errorClassName={'text-center'} />
    </>
  )
}

export default memo(CreateLectureModal)
